import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button, Card } from 'components/ui';
import { LoadingState } from 'components/feedback';
import { useToast } from 'components/toast/ToastProvider';
import { ROUTES } from 'lib/constants';
import { useAddProduct } from '../hooks/useAddProduct';
import { useCatalogItem } from '../hooks/useCatalogItem';
import { ProductForm } from '../components/ProductForm';
import { ProductSearch } from '../components/ProductSearch';
import { catalogItemToFormValues } from '../utils/buildCatalogInput';

/** Add-Product page: optionally search a product to prefill, then submit. */
export function AddProductPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { addProduct, loading } = useAddProduct();

  // When a product is picked from search, load its full item to prefill the form.
  const [selectedId, setSelectedId] = useState(null);
  const { item: selectedItem } = useCatalogItem(selectedId);
  const prefilled = selectedId && selectedItem?.vendorCatalogId === selectedId;
  const isPrefilling = selectedId && !prefilled;

  const handleSubmit = async (input) => {
    try {
      const payload = await addProduct(input);
      if (!payload?.success) {
        toast.error(
          'Could not add product',
          payload?.errors?.[0]?.message ||
            payload?.message ||
            'Please review the form and try again.',
        );
      }
      return payload;
    } catch (err) {
      toast.error('Something went wrong', err.message);
      return null;
    }
  };

  const handleSuccess = (payload) => {
    toast.success('Product added', payload.message || 'Your product is now in your catalog.');
    navigate(ROUTES.CATALOG);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Add product"
        description="Search an existing product to prefill, or fill in the details below."
        actions={
          <Button variant="secondary" onClick={() => navigate(ROUTES.CATALOG)}>
            Back to catalog
          </Button>
        }
      />

      <Card className="mb-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <ProductSearch onSelect={(item) => setSelectedId(item.vendorCatalogId)} />
          {selectedId && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
              Start blank
            </Button>
          )}
        </div>
      </Card>

      {isPrefilling ? (
        <LoadingState label="Loading product…" />
      ) : (
        <ProductForm
          key={prefilled ? selectedItem.vendorCatalogId : 'new'}
          initialValues={prefilled ? catalogItemToFormValues(selectedItem) : undefined}
          submitLabel="Add product"
          onSubmit={handleSubmit}
          submitting={loading}
          onSuccess={handleSuccess}
          onCancel={() => navigate(ROUTES.CATALOG)}
        />
      )}
    </div>
  );
}

export default AddProductPage;
